import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, MapPin, Phone, Linkedin, Twitter, Instagram, Facebook, ArrowRight, 
  ChevronRight, Globe, Shield
} from 'lucide-react';
import { COLORS } from '../utils/colors';

// Enhanced, modern footer component
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Column 1: About */}
          <div>
            <div className="flex items-center mb-6">
              <img src="/logo-white.svg" alt="Lusidea" className="h-10 mr-3" />
            </div>
            <p className="text-gray-400 mb-6">
              Helping forward-thinking organizations identify emerging trends and transform insights 
              into meaningful innovation opportunities.
            </p>
            <div className="flex space-x-4">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-red-700 transition-colors" style={{ backgroundColor: COLORS.primary }}>
                <Twitter className="w-4 h-4 text-white" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-red-700 transition-colors">
                <Linkedin className="w-4 h-4 text-white" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-red-700 transition-colors">
                <Instagram className="w-4 h-4 text-white" />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-red-700 transition-colors">
                <Facebook className="w-4 h-4 text-white" />
              </a>
            </div>
          </div>
          
          {/* Column 2: Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white flex items-center">
                  <ChevronRight className="w-4 h-4 mr-2" />
                  <span>Home</span>
                </Link>
              </li>
              <li>
                <a href="#trends" className="text-gray-400 hover:text-white flex items-center">
                  <ChevronRight className="w-4 h-4 mr-2" />
                  <span>Innovation Trends</span>
                </a>
              </li>
              <li>
                <a href="#approach" className="text-gray-400 hover:text-white flex items-center">
                  <ChevronRight className="w-4 h-4 mr-2" />
                  <span>Our Approach</span>
                </a>
              </li>
              <li>
                <a href="#contact" className="text-gray-400 hover:text-white flex items-center">
                  <ChevronRight className="w-4 h-4 mr-2" />
                  <span>Contact Us</span>
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-gray-400 hover:text-white flex items-center">
                  <ChevronRight className="w-4 h-4 mr-2" />
                  <span>Privacy Policy</span>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Column 3: Contact */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-red-500 mt-0.5 mr-3" style={{color: COLORS.primary}} />
                <span className="text-gray-400">Ulica Franje Petračića 6 <br />10000 Zagreb, Croatia</span>
              </li>
              <li className="flex items-center">
                <Mail className="w-5 h-5 text-red-500 mr-3" style={{color: COLORS.primary}} />
                <a href="mailto:hello@lusidea.com" className="text-gray-400 hover:text-white">hello@lusidea.com</a>
              </li>
              <li className="flex items-center">
                <Globe className="w-5 h-5 text-red-500 mr-3" style={{color: COLORS.primary}} />
                <a href="https://lusidea.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">www.lusidea.com</a>
              </li>
            </ul>
          </div>
          
          {/* Column 4: Newsletter */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Innovation Newsletter</h4>
            <p className="text-gray-400 mb-4">
              Stay updated with the latest innovation trends and insights.
            </p>
            <form className="mb-4">
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-grow px-4 py-2 bg-gray-800 text-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <button
                  type="submit"
                  className="px-4 py-2 rounded-r-lg text-white flex items-center justify-center"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </form>
            <p className="text-gray-500 text-xs flex items-start">
              <Shield className="w-3.5 h-3.5 mt-0.5 mr-1.5 flex-shrink-0" />
              <span>We respect your privacy and never share your information.</span>
            </p>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 mb-4 md:mb-0">
            © {currentYear} Lusidea. All rights reserved.
          </p>
          
          <div className="flex space-x-6">
            <a href="/terms" className="text-gray-500 hover:text-white text-sm">Terms of Service</a>
            <a href="/privacy" className="text-gray-500 hover:text-white text-sm">Privacy Policy</a>
            <a href="/cookies" className="text-gray-500 hover:text-white text-sm">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;